/* eslint-disable react/jsx-props-no-spreading */
import React, {
  useState,
  useEffect,
  useRef,
  useMemo,
} from 'react';
import PropTypes from 'prop-types';
import Moment from 'moment';
import {
  Col,
  DatePicker,
  Form,
  Input,
  Row,
  Spin,
  TimePicker,
  Typography,
} from 'antd';
import Select from 'components/Select';
import {
  afectacionesEnum,
  dataTypesEnum,
  getEnumValues,
  padronesEnum as PADRONES,
  padronesEnum,
  solicitantesEnum,
} from 'views/Tramites/GPM/enums';
import gettersMap, { gettersByPadronByProp } from 'views/Tramites/GPM/configsPadrones/getters';
import {
  INTEGER_PATTERN,
  toInteger,
  decimalNormalizer,
  toSlug,
  getDistinctValues,
  toMoment,
  excelSerialToMoment,
} from 'utils/normalizers';
import {
  DATE_FORMAT,
  DATETIME_FORMAT_ALT,
  DATETIME_FRONTEND_FORMAT,
  DATE_FRONTEND_FORMAT,
  TIME_FORMAT,
} from 'utils/formatValues';
import { SLUG_PATTERN, createDecimalRegExp } from 'utils/patterns';
import {
  configs as _configs,
  fieldsInfo,
  forbiddenProps,
  sharedForbiddenProps,
  tabs,
} from 'views/Tramites/GPM/configsPadrones/index';
import LadaNumero from 'components/LadaNumero';
import AvatarUploader from 'components/AvatarUploader';
import { consultaRepuveNacionalFieldMap } from 'api/recaudacion/repuve';
import { useSelector } from 'react-redux';
import toCurrency from 'utils/toCurrency';
import { getContenTypePorTipoDePadron } from 'utils/padrones';
import NumericInput from 'components/NumericInput';
import getParamsPadron from '../paramsPadronesAfectados';
import { valuesRepuveToLocales } from '../repuve';

const REPUVE = process.env.REACT_APP_ENV_NAME === 'repuve';
const TLAJO = process.env.REACT_APP_ENV_NAME === 'tlajomulco';
const stringDataTypes = [
  dataTypesEnum.CHAR,
  dataTypesEnum.TEXT,
  dataTypesEnum.SLUG,
  dataTypesEnum.URL,
  dataTypesEnum.EMAIL,
];
const integerNumberDataTypes = [
  dataTypesEnum.POSITIVE_INTEGER,
  dataTypesEnum.POSITIVE_BIG_INTEGER,
  dataTypesEnum.POSITIVE_SMALL_INTEGER,
  dataTypesEnum.INTEGER,
  dataTypesEnum.BIG_INTEGER,
  dataTypesEnum.SMALL_INTEGER,
];
const nonIntegerNumberDataTypes = [dataTypesEnum.DECIMAL, dataTypesEnum.FLOAT];
const dateTypes = [dataTypesEnum.DATE, dataTypesEnum.DATE_TIME, dataTypesEnum.TIME];
const plainInputDataTypes = [
  ...stringDataTypes,
  ...integerNumberDataTypes,
  ...nonIntegerNumberDataTypes,
];

const requireNormalizeTypes = [
  dataTypesEnum.DECIMAL,
  dataTypesEnum.SLUG,
  ...integerNumberDataTypes,
];

const normalizeValue = (value, tipoDeDato) => {
  if (integerNumberDataTypes.includes(tipoDeDato)) {
    return toInteger(value);
  } if (tipoDeDato === dataTypesEnum.DECIMAL) {
    return decimalNormalizer(value);
  } if (tipoDeDato === dataTypesEnum.SLUG) {
    return toSlug(value);
  }
  return value;
};

// DatePicker/TimePicker requieren Moment, datos de json_predio pueden venir como string o
// número serial de Excel (cargas masivas)
const toDateFieldValue = (value) => {
  if (value === null || value === undefined || value === '') return null;
  if (Moment.isMoment(value)) return value;
  if (typeof value === 'number') return excelSerialToMoment(value);
  if (typeof value === 'string') {
    return toMoment(value, [DATE_FORMAT, DATE_FRONTEND_FORMAT, DATETIME_FORMAT_ALT, 'HH:mm:ss']);
  }
  return null;
};

const getPattern = (tipoDeDato, decimalPlaces, regex = null) => {
  if (integerNumberDataTypes.includes(tipoDeDato)) {
    return INTEGER_PATTERN;
  } if (nonIntegerNumberDataTypes.includes(tipoDeDato)) {
    return createDecimalRegExp(null, { post: decimalPlaces })[0];
  }
  if (tipoDeDato === dataTypesEnum.SLUG) {
    return SLUG_PATTERN || regex;
  }
  return regex;
};

const getPatternErrorMessage = (tipoDeDato) => {
  if (integerNumberDataTypes.includes(tipoDeDato)) {
    return 'Ingrese un número entero válido';
  } if (nonIntegerNumberDataTypes.includes(tipoDeDato)) {
    return 'Ingrese un número decimal válido';
  } if (tipoDeDato === dataTypesEnum.SLUG) {
    return 'Ingrese un slug válido';
  }
  return null;
};

const getFieldControl = (
  field,
  refDataSources,
  formInstance,
  fields,
  tipoDeAfectacion,
  tabname,
  updateUI,
  setLoadingPartial,
  padron,
  disabled,
  tipoDePadron,
  plantillaDeTramite,
) => {
  if (!field.hasConfig || field.config.hasntConfig) {
    return [
      Input,
      {
        placeholder: 'Missing config',
        disabled: true,
      },
    ];
  }
  const {
    tipoDeDato,
    maxLength: _maxLength,
    config,
    model,
  } = field;
  const maxLength = config.controlProps?.maxLength || _maxLength;
  const [nameSpace, modelName] = model?.split('.') || [];
  const getter = model && gettersMap[nameSpace]?.[modelName];
  if (config.relatedConfig || config.showIf) {
    const values = formInstance.getFieldValue();
    if ((config.relatedConfig?.hideOnFalsy && !values[config.relatedConfig.name])
      || (config.relatedConfig?.hideOnTruty && values[config.relatedConfig.name])
      || (config.showIf && !config.showIf(values))) {
      return [null, { hidden: true }];
    }
  }
  if (config.advanced) {
    const { control, ..._config } = config;
    const onClear = () => {
      // eslint-disable-next-line no-param-reassign
      refDataSources.current[modelName] = [];
      formInstance.setFieldsValue({ [field.name]: undefined });
    };
    _config.name = `q_${field.name}`;
    if (!getter) {
      _config.inputProps = { placeholder: !getter ? `Getter no definido ${model}` : null };
    }
    return [
      control,
      {
        disabled: !!config.controlProps?.disabled || !getter,
        ..._config,
        getter,
        resultProps: {
          ...config.resultProps,
          onClear,
          setDataSource: (record) => {
            // eslint-disable-next-line no-param-reassign
            refDataSources.current[modelName] = record ? [record] : [];
          },
          dataSource: refDataSources.current[modelName],
        },
      },
    ];
  }
  const defOnChange = async (value) => {
    const asParamInFields = fields.filter((f) => f.config?.params?.includes(field.name)
      && !f.config?.fkListIsPaginated);
    if (asParamInFields.length) {
      const promises = asParamInFields.map((f) => {
        const [_nameSpace, _modelName] = f.model?.split('.') || [];
        const _getter = model && gettersMap[_nameSpace]?.[_modelName];
        return [
          _modelName,
          _getter,
          f.config.paramsEq?.[f.config.params.indexOf(field.name)] || field.name,
        ];
      });
      const validPromises = promises.filter(([, _getter]) => _getter);
      if (validPromises.length) {
        const responses = value
          ? await Promise.all(validPromises.map(([, _getter, q]) => _getter({ [q]: value })))
          : validPromises.map(() => []);
        responses.forEach((res, idx) => {
          // eslint-disable-next-line no-param-reassign
          refDataSources.current[validPromises[idx][0]] = res;
        });
      }
      formInstance.resetFields(asParamInFields.map((f) => f.name));
      setTimeout(updateUI);
    }
  };
  const formatProps = (controlProps = {}) => {
    const props = { ...controlProps, disabled: (model && !getter) };

    ['onBlur', 'onChange', 'onClear'].forEach((func) => {
      if (controlProps[func]) {
        props[func] = (...evtArgs) => {
          controlProps[func]({
            formInstance,
            evtArgs,
            dataSources: refDataSources.current,
            padron,
          });
        };
      }
    });
    if (!props.onChange
      && [dataTypesEnum.FOREIGN_KEY, dataTypesEnum.BOOLEAN].includes(tipoDeDato)) {
      props.defOnChange = true;
      props.onChange = defOnChange;
      props.onClear = defOnChange;
    }
    if (maxLength) {
      props.maxLength = maxLength;
    }
    if (config.disabledEnAlta && tipoDeAfectacion === afectacionesEnum.ALTA) {
      props.disabled = true;
    }
    if (typeof controlProps.disabled === 'boolean') {
      props.disabled = props.disabled || controlProps.disabled;
    }
    if (typeof controlProps.disabled === 'function') {
      props.disabled = props.disabled || controlProps.disabled({
        tabname,
        formInstance,
        plantillaDeTramite,
      });
    }
    if (field.name === 'descripcion' && tipoDePadron !== 3) {
      props.disabled = true;
    }
    if (controlProps.addonAfter) {
      props.addonAfter = controlProps.addonAfter(
        formInstance,
        tabname,
        props.disabled || disabled,
        tipoDeAfectacion,
        setLoadingPartial,
      );
    }
    return props;
  };
  if (tipoDeDato === dataTypesEnum.TEXT) {
    return [
      Input.TextArea,
      {
        allowClear: true,
        disabled: !!config.controlProps?.disabled,
        // TODO: revisar porque se desfasan los controles
        // autoSize: config.controlProps?.autoSize,
        autoSize: { minRows: 2, maxRows: 2 },
        ...formatProps(config.controlProps),
      },
    ];
  } if (plainInputDataTypes.includes(tipoDeDato)) {
    return [
      Input,
      {
        allowClear: true,
        disabled: !!config.controlProps?.disabled,
        ...formatProps(config.controlProps),
      },
    ];
  } if (tipoDeDato === dataTypesEnum.BOOLEAN) {
    return [
      Select,
      {
        trueFalse: config.controlProps?.trueFalse || true,
        disabled: !!config.controlProps?.disabled,
        onClear: defOnChange,
        onChange: defOnChange,
        ...formatProps(config.controlProps),
      },
    ];
  } if (tipoDeDato === dataTypesEnum.MANY_TO_MANY) {
    return [
      Select,
      {
        mode: 'multiple',
        placeholder: !getter ? `Getter no definido ${model}` : null,
        dataSource: refDataSources.current[modelName] || [],
        ...formatProps(config.controlProps),
      },
    ];
  } if (tipoDeDato === dataTypesEnum.FOREIGN_KEY) {
    if (model === 'usuarios.Lada') return [LadaNumero, formatProps(config.controlProps)];
    const controlProps = { ...config.controlProps };
    const hasAllParams = config.params?.every((param) => param === '' || formInstance.getFieldValue(param));
    controlProps.disabled = !!controlProps?.disabled || !getter || (config.params && !hasAllParams);
    if (!getter) {
      controlProps.placeholder = `Getter no definido ${model}`;
    }
    if (nameSpace && modelName && config.fkListIsPaginated && getter) {
      controlProps.dataSource = refDataSources.current[modelName] || { results: [] };
      const selfValue = formInstance.getFieldValue(field.name);
      const params = formInstance.getFieldsValue(config.params);
      config.paramsEq?.forEach((p, idx) => {
        params[p] = params[config.params[idx]];
        delete params[config.params[idx]];
      });
      // TODO: habia un if rodeando controlProps.pagination pero eso evitaba que se llenara
      // el select con los datos al editar un registro, tener en cuenta si truena algo relacionado
      controlProps.pagination = {
        getter,
        setter: (dataSource) => {
          // eslint-disable-next-line no-param-reassign
          refDataSources.current[modelName] = dataSource;
        },
        config: {
          auto: !config.params || hasAllParams,
          selfValue: selfValue
            && controlProps.dataSource.results?.some((e) => e.id === selfValue),
          qName: config.controlProps?.labelProp || 'descripcion',
          params,
        },
      };
      if (config.params) {
        controlProps.pagination.params = Object.fromEntries(config.params
          ?.map((param) => [param, formInstance.getFieldValue(param)]));
      }
    } else if (nameSpace && model) {
      controlProps.dataSource = refDataSources.current[modelName] || [];
    }
    if (controlProps.normalizeDataSource) {
      controlProps.dataSource = controlProps
        .normalizeDataSource(refDataSources.current[modelName], formInstance);
      delete controlProps.normalizeDataSource;
    }
    return [Select, formatProps(controlProps)];
  } if (tipoDeDato === dataTypesEnum.IMAGE) {
    return [AvatarUploader, {}];
  } if (dateTypes.includes(tipoDeDato)) {
    const _props = {
      ...config.controlProps,
      format: DATE_FRONTEND_FORMAT,
      disabled: !!config.controlProps?.disabled,
      placeholder: '',
    };
    if (tipoDeDato === dataTypesEnum.DATE_TIME && config?.controlProps?.showTime !== false) {
      _props.showTime = {
        format: TIME_FORMAT,
      };
      _props.format = DATETIME_FRONTEND_FORMAT;
    }
    if (tipoDeDato === dataTypesEnum.TIME) {
      _props.format = TIME_FORMAT;
      return [TimePicker, formatProps(_props)];
    }
    return [DatePicker, formatProps(_props)];
  }
  return [
    Input,
    {
      placeholder: `Tipo de dato "${tipoDeDato}" no soportado`,
      disabled: true,
      className: 'warn',
    },
  ];
};

const getFormItemProps = (field, formInstance) => {
  const {
    hidden,
    regex,
    max,
    min,
    tipoDeDato,
    required,
    // maxLength: _maxLength,
    decimalPlaces,
    config,
  } = field;
  const rules = [...(config?.formItemProps?.rules || [])];
  const formItemProps = {};
  if (hidden || config.hidden) {
    formItemProps.hidden = true;
  }
  if (config.formItemProps?.getTooltip) {
    formItemProps.shouldUpdate = (prev, curr) => prev[field.name] !== curr[field.name];
  }
  if (config.formItemProps?.validateTrigger) {
    formItemProps.validateTrigger = config.formItemProps?.validateTrigger;
  }
  if (config.formItemProps?.getTooltip) {
    formItemProps.shouldUpdate = (prev, curr) => prev[field.name] !== curr[field.name];
  }
  if (config.updateOnChange || config.params?.length) {
    const { updateOnChange, params = [] } = config;
    const propsToWatch = [...params, updateOnChange].filter((p) => p);
    formItemProps.shouldUpdate = (prev, curr) => propsToWatch.some((p) => prev[p] !== curr[p]);
  }
  if (config.formItemProps?.tooltip) {
    formItemProps.tooltip = config.formItemProps.tooltip;
  }
  if (config.formItemProps?.required || (required && !config.ignoreBaseRequired)) {
    if (config.formItemProps?.required !== false
      && !config.formItemProps?.rules?.some((r) => r.required === false)) {
      rules.push({ required: config.formItemProps?.required ?? required });
    }
  }
  const pattern = getPattern(tipoDeDato, decimalPlaces, regex);
  if (pattern) {
    rules.push({
      pattern,
      message: getPatternErrorMessage(tipoDeDato),
    });
  }
  if (stringDataTypes.includes(tipoDeDato)) {
    if (max && !rules.some((rule) => 'max' in rule)) {
      rules.push({ max });
    }
    if (min && !rules.some((rule) => 'min' in rule)) {
      rules.push({ min });
    }
  }
  if (rules.length) {
    formItemProps.rules = [...rules];
  }
  if (config.relatedConfig || config.params) {
    const { relatedConfig, params = [] } = config;
    formItemProps.shouldUpdate = (prev, curr) => params.some((p) => prev[p] !== curr[p])
      || (relatedConfig && prev[relatedConfig.name] !== curr[relatedConfig.name]);
  }
  if (field.label) {
    formItemProps.label = field.label;
  }
  if (config.formItemProps?.label) {
    formItemProps.label = typeof config.formItemProps.label === 'function'
      ? config.formItemProps.label(formInstance) : config.formItemProps.label;
  }
  if (config.formItemProps?.normalize) {
    formItemProps.normalize = (val) => config.formItemProps.normalize(val);
  } else if (requireNormalizeTypes.includes(tipoDeDato)) {
    formItemProps.normalize = (value) => normalizeValue(value, tipoDeDato);
  }
  formItemProps.colProps = config.colProps || {};
  if (field.model === 'usuarios.Lada' || tipoDeDato === dataTypesEnum.IMAGE) {
    delete formItemProps.label;
  }
  return formItemProps;
};

const normalizeFields = (fields, tipoDePadron) => {
  const configs = typeof _configs[tipoDePadron] === 'function'
    ? _configs[tipoDePadron]() : _configs[tipoDePadron];
  const normalized = fields.map(({
    id,
    label_de_descripcion: label,
    tipo_de_dato_del_campo: tipoDeDato,
    nombre_en_la_bd: name,
    max_digits: max,
    min_digits: min,
    null: allowNull,
    max_length: maxLength,
    decimal_places: decimalPlaces,
    model,
    hidden,
    blank,
  }) => ({
    id,
    label,
    tipoDeDato,
    name,
    max,
    min,
    required: !allowNull,
    maxLength,
    decimalPlaces,
    model,
    hidden,
    blank,
    config: configs[name] || {},
    hasConfig: !!configs[name],
  }));

  return normalized.map((field) => {
    const related = normalized.find((f) => f.config?.hideOnFalsy?.includes(field.name)
      || f.config?.hideOnTruty?.includes(field.name));
    const relatedConfig = related ? { name: related.name } : null;
    if (related) {
      if (related.config.hideOnFalsy?.includes(field.name)) {
        relatedConfig.hideOnFalsy = true;
      }
      if (related.config.hideOnTruty?.includes(field.name)) {
        relatedConfig.hideOnTruty = true;
      }
    }
    return {
      ...field,
      config: {
        ...field.config,
        relatedConfig,
      },
    };
  });
};

const mapFields = (tipoDePadron, fields) => fields.map((field, idx) => {
  if (tipoDePadron === PADRONES.CONTRIBUYENTE && idx === 0) {
    if (fields.some((f) => f.nombre_en_la_bd === 'tipo_de_persona')) {
      return [fieldsInfo[tipoDePadron].tipo_de_persona_alt, field];
    }
  }
  if (field.nombre_en_la_bd === 'linea') {
    return [fieldsInfo[tipoDePadron].marca, field];
  }
  if (field.nombre_en_la_bd === 'seccion_de_localidad') {
    return [fieldsInfo[tipoDePadron].clave_catastral_municipal, field];
  }
  if (field.nombre_en_la_bd === 'contribuyente_propietario') {
    return [fieldsInfo[tipoDePadron].es_propietario_ciudadano, field];
  }
  if (field.nombre_en_la_bd === 'valor_catastral_calculado' && TLAJO) {
    return [fieldsInfo[tipoDePadron].valor_catastral_calculado];
  }
  if (tipoDePadron === padronesEnum.EMPRESA && TLAJO && idx === 0) {
    return [fieldsInfo[tipoDePadron]._giros_comerciales, field];
  }
  if (tipoDePadron === padronesEnum.MODULOS && TLAJO && idx === 0) {
    return [fieldsInfo[tipoDePadron]._giros_de_modulos, field];
  }
  return field;
});

const RenderFieldsByConfig = ({
  fields: __fields,
  tipoDeAfectacion,
  setLoading,
  tipoDePadron,
  padron,
  disabled,
  tipoDePadronSolicitante,
  padronSolicitante,
  valuesRepuve: _valuesRepuve,
  setVisibleModalInfoRepuve,
  withTitles,
  valuesComplementos,
  tramite,
  recibo,
  plantillaDeTramite,
}) => {
  const user = useSelector((s) => s.auth.user);
  const _forbProps = forbiddenProps[tipoDePadron] || [];
  const _fields = !window.location.pathname.includes('/tramites/') ? __fields
    : __fields.filter((f) => !sharedForbiddenProps.includes(f.nombre_en_la_bd)
      && !_forbProps.includes(f.nombre_en_la_bd));
  const formInstance = Form.useFormInstance();
  const valuesRepuve = Form.useWatch('valuesRepuve', formInstance);
  const autofill = Form.useWatch('autofill', formInstance);
  const refDataSources = useRef({});
  const [hasAllDataSources, setHasAllDataSources] = useState(false);
  const [updateUI, setUpdateUI] = useState(0);
  const [loadingPartial, setLoadingPartial] = useState(false);

  const fields = useMemo(() => {
    if (!tipoDePadron) {
      return [];
    }
    const mapped = mapFields(tipoDePadron, _fields).flat().filter((e) => e);
    const normalized = normalizeFields(mapped, tipoDePadron);
    return normalized;
  }, [_fields]);

  const autoFillfromRepuve = async (_values = valuesRepuve) => {
    setLoadingPartial(true);
    const values = await valuesRepuveToLocales(_values, refDataSources.current);
    formInstance.resetFields(fields.map((f) => f.name));
    values.autofill = false;
    setTimeout(() => {
      setVisibleModalInfoRepuve(false);
      setLoadingPartial(false);
      formInstance.setFieldsValue(values);
    });
  };

  useEffect(() => { if (autofill) autoFillfromRepuve(); }, [autofill]);

  useEffect(() => {
    if (valuesRepuve && !_valuesRepuve) setVisibleModalInfoRepuve('RenderFieldsByConfig');
  }, [valuesRepuve]);

  const tabsPadron = typeof tabs[tipoDePadron] === 'function'
    ? tabs[tipoDePadron]() : tabs[tipoDePadron] || [];

  const mappedTabs = tabsPadron.map((tab) => {
    const tabFields = tab.fields.map((tf) => fields.find((f) => f.name === tf)).filter((f) => f);
    return { title: tab.title, fields: tabFields };
  });

  const _tabs = mappedTabs.filter((tab) => {
    if (!tab.fields.length) return false; // ocultar si no hay fields de ese tab
    const allRepeated = tab.fields.every((f) => mappedTabs.some((_t) => {
      if (_t.title === tab.title) return false;
      return _t.fields.find((_f) => _f.name === f.name);
    }));
    if (allRepeated) return false; // ocultar si todos los fields de ese tab aparecen en otro tab
    return true;
  });

  const validFields = _tabs.map((t) => t.fields).flat();

  const fieldCiudadano = validFields.find((f) => f.model === 'cuentaunicasir.Ciudadano');
  const fieldContribuyente = validFields.find((f) => f.model === 'empresa.Contribuyente');
  const noReCiudadano = { id: null, nombre_completo: 'No relacionar ciudadano' };
  const noReContribuyente = { id: null, razon_social: 'No relacionar contribuyente' };

  const withNoRe = (noRelation, _padron) => {
    if (tipoDePadron === PADRONES.EMPRESA) {
      return _padron ? [noRelation, _padron] : [noRelation];
    }
    return _padron ? [_padron] : [];
  };

  const getValuesPropietario = async () => {
    if (tipoDePadron === PADRONES.PREDIO
      || tipoDeAfectacion !== afectacionesEnum.ALTA || disabled || !padronSolicitante) return {};

    const values = {};
    if (tipoDePadronSolicitante === PADRONES.CIUDADANO) {
      if (fieldCiudadano) {
        refDataSources.current.Ciudadano = withNoRe(noReCiudadano, padronSolicitante);
        values[fieldCiudadano.name] = padronSolicitante.id;
      }
      if (fieldContribuyente) {
        const [contribuyente] = (await gettersMap.empresa
          .Contribuyente({ ciudadano: padronSolicitante.id })).results;
        if (contribuyente) {
          refDataSources.current.Contribuyente = withNoRe(noReContribuyente, contribuyente);
          values[fieldContribuyente.name] = contribuyente.id;
        }
      }
    }
    if (tipoDePadronSolicitante === PADRONES.CONTRIBUYENTE) {
      if (fieldContribuyente) {
        refDataSources.current.Contribuyente = withNoRe(noReContribuyente, padronSolicitante);
        values[fieldContribuyente.name] = padronSolicitante.id;
      }
      if (fieldCiudadano && padronSolicitante.ciudadano) {
        const [ciudadano] = (await gettersMap.cuentaunicasir
          .Ciudadano({ id: padronSolicitante.ciudadano })).results;
        if (ciudadano) {
          refDataSources.current.Ciudadano = withNoRe(noReCiudadano, ciudadano);
          values[fieldCiudadano.name] = ciudadano.id;
        }
      }
    }
    if (fieldCiudadano && !values[fieldCiudadano.name]) {
      values[fieldCiudadano.name] = null;
      refDataSources.current.Ciudadano = withNoRe(noReCiudadano);
    }
    if (fieldContribuyente && !values[fieldContribuyente.name]) {
      refDataSources.current.Contribuyente = withNoRe(noReContribuyente);
      values[fieldContribuyente.name] = null;
    }
    return values;
  };

  useEffect(() => {
    const getDefaultValues = () => {
      if (tipoDeAfectacion === afectacionesEnum.ALTA && !padron && !disabled) {
        const values = Object.fromEntries(
          fields.filter((field) => 'defaultValue' in field.config).map((field) => [
            field.name,
            typeof field.config.defaultValue === 'function'
              ? field.config.defaultValue({ tramite, recibo, user }) : field.config.defaultValue,
          ]).filter(([, v]) => v !== undefined),
        );
        return values;
      }
      return {};
    };

    const getOverrideValues = () => {
      if (tipoDeAfectacion === afectacionesEnum.MODIFICACION && !disabled) {
        const values = Object.fromEntries(
          fields.filter((field) => 'overrideValue' in field.config).map((field) => [
            field.name,
            typeof field.config.overrideValue === 'function'
              ? field.config.overrideValue({
                tramite,
                recibo,
                user,
                padronSolicitante,
                formInstance,
              }) : field.config.overrideValue,
          ]).filter(([, v]) => v !== undefined),
        );
        return values;
      }
      return {};
    };

    const init = async () => {
      setLoading(true);
      const values = {};
      const dataTypesToGet = [dataTypesEnum.FOREIGN_KEY, dataTypesEnum.MANY_TO_MANY];
      const modelsToGet = validFields
        .filter((f) => {
          if (!dataTypesToGet.includes(f.tipoDeDato)) return false;
          const [nameSpace, modelName] = f.model.split('.');
          const getter = gettersByPadronByProp[tipoDePadron]?.[f.name]
            || gettersMap[nameSpace]?.[modelName];
          if (!getter) return false;
          if (padron) {
            // fks with params
            if (f.config.params?.length) return typeof padron?.[f.name] === 'number';
            // advanced search fields
            if (f.config.advanced) return typeof padron?.[f.name] === 'number';
            // fks without params
            return true;
          }
          return !f.config.advanced && !f.config.params?.length;
        })
        .map(({ model, name, config: c }) => ({
          model,
          name,
          advanced: c?.advanced || c.params?.length,
        }));
      if (modelsToGet.length) {
        const promises = modelsToGet.map(({ model, advanced, name }) => {
          let params = {};
          if (name === 'valor_catastral_calculado') {
            params = {
              aplica_avaluo: true,
              estados_globales: 4,
              object_id_padron: padron.id,
              content_type_a_padron: getContenTypePorTipoDePadron(tipoDePadron),
            };
          }
          const [nameSpace, modelName] = model.split('.');
          const getter = gettersByPadronByProp[tipoDePadron]?.[name]
            || gettersMap[nameSpace]?.[modelName];
          if (advanced) return getter({ id: padron[name] });
          return getter(params);
        });
        const dataSources = await Promise.all(promises);
        modelsToGet.forEach(({ model }, idx) => {
          const [, modelName] = model.split('.');
          const ds = dataSources[idx]?.results || dataSources[idx];
          refDataSources.current[modelName] = Array.isArray(ds) ? ds : [];
          if (modelsToGet[idx].name === 'valor_catastral_calculado') {
            refDataSources.current[modelName] = refDataSources.current[modelName].map((t) => {
              const json = JSON.parse(t.inf_final_padron || '{}');
              const valor_catastral = parseFloat(json?.valor_catastral).toFixed(2);
              return {
                json,
                id: `${t.folio}_${valor_catastral}`,
                descripcion: [
                  t.folio,
                  toMoment(t.fecha_de_finalizacion)?.format(DATE_FRONTEND_FORMAT),
                  toCurrency(valor_catastral),
                ].join(' - '),
              };
            });
          }
        });
        if (padron?.linea) {
          values.marca = refDataSources.current.LineaVehicular?.[0]?.marca;
        }
        if (padron?.tipo_de_persona) {
          values.tipo_de_persona_alt = (REPUVE
            ? { 1: 0, 2: true, 3: false } : { 1: true, 2: false })[padron.tipo_de_persona];
        }
      }
      if (padron) {
        const advancedFields = getDistinctValues(validFields.filter((f) => f.config.advanced)
          .map(({ model, name }) => ({ model, name })));
        [...advancedFields, ...modelsToGet].forEach(({ model, name }) => {
          const modelName = model.split('.')[1];
          if (padron[name] && typeof padron[name] === 'object' && !Array.isArray(padron[name])) {
            refDataSources.current[modelName] = [padron[name]];
          }
        });
      }
      const defaultValues = getDefaultValues();
      const overrideValues = getOverrideValues();
      const valuesPropietario = await getValuesPropietario();

      setLoading(false);
      setHasAllDataSources(true);
      const currValues = padron ? Object.fromEntries(Object.keys(padron)
        .map((key) => ([key, padron[key]?.id || padron[key]]))) : {};
      if (TLAJO && tipoDeAfectacion === afectacionesEnum.MODIFICACION
        && tipoDePadron === padronesEnum.PREDIO) {
        if (currValues.valor_catastral_calculado) {
          if (tramite.folio_trm_repuve) {
            currValues.valor_catastral_calculado = `${tramite.folio_trm_repuve}_${parseFloat(currValues.valor_catastral_calculado).toFixed(2)}`;
          } else {
            delete currValues.valor_catastral_calculado;
          }
        }
      }
      const initialValues = {
        ...values,
        ...defaultValues,
        ...valuesPropietario,
        valuesRepuve: _valuesRepuve,
        ...currValues,
        // TODO: solo pasar valuesComplementos de variables capturadas antes del paso actual
        ...valuesComplementos,
        ...overrideValues,
      };
      validFields
        .filter((f) => dateTypes.includes(f.tipoDeDato) && f.name in initialValues)
        .forEach(({ name }) => {
          initialValues[name] = toDateFieldValue(initialValues[name]);
        });
      formInstance.setFieldsValue(initialValues);
      if (_valuesRepuve) autoFillfromRepuve(_valuesRepuve);
    };

    if (tipoDePadron) init();
  }, []);

  const isDisabledRepuve = (name) => {
    if (REPUVE
      && tipoDeAfectacion === afectacionesEnum.ALTA
      && tipoDePadron === PADRONES.VEHICULO
      && window.location.pathname.includes('tramites/registrar-tramite')) {
      const esCampoBusqueda = Object.keys(consultaRepuveNacionalFieldMap).includes(name);
      // no se ha hecho la consulta, permitir solo capturar los campos de busqueda
      if (valuesRepuve === undefined) return !esCampoBusqueda;
      // se hizo la consulta y se encontro el vehiculo, deshabilitar niv
      if (valuesRepuve) return name === 'numero_de_identificacion_vehicular';
      // se hizo la consulta y no se encontro el vehiculo, habilitar todo el formulario
      if (valuesRepuve === false) return false;
    }
    // si no es alta vehiculo de repuve no deshabilitar
    return false;
  };

  const renderFields = (fieldsToRender, tabname) => fieldsToRender.map((field) => {
    const getFieldControlArgs = [
      field,
      refDataSources,
      formInstance,
      fields,
      tipoDeAfectacion,
      tabname,
      () => setUpdateUI(updateUI + 1),
      setLoadingPartial,
      padron,
      disabled,
      tipoDePadron,
      plantillaDeTramite,
    ];
    const { hidden, tipoDeDato, name } = field;
    if (field.config.advanced) {
      let [Control, controlProps] = getFieldControl(...getFieldControlArgs);
      const resultName = field.config.resultProps.name;
      const relatedConfigName = controlProps.relatedConfig?.name;
      const formItemProps = getFormItemProps(field, formInstance);
      if (tipoDeAfectacion === afectacionesEnum.CONSULTA) {
        delete formItemProps.rules;
      }
      if (controlProps.autoSize) {
        formItemProps.className = 'textarea';
      }
      return (
        <Form.Item
          noStyle
          shouldUpdate={(prev, curr, info) => {
            if (formItemProps?.shouldUpdate?.(prev, curr)) return true;
            if (info.source === 'internal') {
              if (name === 'version_vehicular') return prev.linea !== curr.linea;
              return false;
            }
            if (relatedConfigName) return prev[relatedConfigName] !== curr[relatedConfigName];
            return prev[resultName] !== curr[resultName];
          }}
          key={`_${field.id}_${tabname}_fi`}
        >
          {() => {
            [Control, controlProps] = getFieldControl(...getFieldControlArgs);
            if (hidden || formItemProps.hidden || controlProps.hidden) return null;
            if (field.config.advancedComponentName === 'BusquedaAvanzadaPadrones') {
              controlProps.advancedComponentProps.extraParams = getParamsPadron(
                {
                  tipo_de_padron: field.config.advancedComponentProps.tipoDePadron,
                  es_propietario: true,
                },
                tipoDePadronSolicitante,
                padronSolicitante,
              );
            }
            if (disabled) {
              controlProps.disabled = true;
              controlProps.resultProps.onClear = false;
            }
            if (isDisabledRepuve(name)) {
              controlProps.disabled = true;
            }
            // if (name === 'version_vehicular') {
            //   const linea = formInstance.getFieldValue('linea');
            //   controlProps.disabled = !linea;
            //   controlProps.advancedComponentProps = {
            //     linea: refDataSources.current.LineaVehicular?.find((l) => l.id === linea),
            //   };
            // }
            return <Control {...controlProps} />;
          }}
        </Form.Item>
      );
    }
    const { shouldUpdate, colProps, ...formItemProps } = getFormItemProps(field, formInstance);
    if (shouldUpdate) {
      return (
        <Form.Item noStyle shouldUpdate={shouldUpdate} key={`${field.id}_${tabname}_fi`}>
          {() => {
            const [Control, controlProps] = getFieldControl(...getFieldControlArgs);
            formItemProps.label = getFormItemProps(field, formInstance).label;
            formItemProps.rules = getFormItemProps(field, formInstance).rules;
            if (controlProps.autoSize) {
              formItemProps.className = 'textarea';
            }
            if (controlProps.hidden) {
              return null;
            }
            if (disabled) {
              controlProps.disabled = true;
            }
            const _hidden = hidden || formItemProps.hidden || false;
            if (!_hidden) {
              colProps.xs = 24;
              colProps.sm = 24;
              colProps.md = colProps.md || (tipoDeDato === dataTypesEnum.TEXT ? 16 : 8);
            }
            if (isDisabledRepuve(name)) {
              controlProps.disabled = true;
            }
            if (tipoDeAfectacion === afectacionesEnum.CONSULTA) {
              delete formItemProps.rules;
            }
            return (
              <Col {...colProps}>
                <Form.Item name={name} {...formItemProps} hasFeedback>
                  <Control {...controlProps} />
                </Form.Item>
              </Col>
            );
          }}
        </Form.Item>
      );
    }
    const [Control, controlProps] = getFieldControl(...getFieldControlArgs);
    if (disabled) {
      controlProps.disabled = true;
    }
    if (isDisabledRepuve(name)) {
      controlProps.disabled = true;
    }
    const _hidden = hidden || controlProps.hidden || formItemProps.hidden || false;
    if (!_hidden) {
      colProps.xs = 24;
      colProps.sm = 24;
      colProps.md = colProps.md || (tipoDeDato === dataTypesEnum.TEXT ? 16 : 8);
    } else {
      colProps.style = { padding: 0 };
    }
    if (tipoDeAfectacion === afectacionesEnum.CONSULTA) {
      delete formItemProps.rules;
    }
    if (controlProps.autoSize) {
      formItemProps.className = 'textarea';
    }
    if (nonIntegerNumberDataTypes.includes(tipoDeDato)) {
      controlProps.decimal = true;
      controlProps.allowZero = controlProps.allowZero ?? !field.required;
    }
    return (
      <Col key={`${field.id}_${tabname}_col`} {...colProps}>
        {nonIntegerNumberDataTypes.includes(tipoDeDato) ? (
          <NumericInput name={name} {...formItemProps} {...controlProps} allowClear={false} />
        ) : (
          <Form.Item name={name} {...formItemProps} hasFeedback>
            <Control {...controlProps} />
          </Form.Item>
        )}
      </Col>
    );
  });

  if (hasAllDataSources) {
    if (!_tabs.length && !disabled) {
      return (
        <Form.Item name="__no_fields_error__" rules={[{ required: true }]} hidden>
          <Input />
        </Form.Item>
      );
    }

    const altaVehiculoRepuve = REPUVE && tipoDePadron === PADRONES.VEHICULO
      && tipoDeAfectacion === afectacionesEnum.ALTA;

    return (
      <Spin spinning={loadingPartial}>
        <Row gutter={10}>
          {_tabs.map((tab) => (
            <React.Fragment key={tab.title}>
              {tab.title && window.location.pathname.includes('/tramites/') && withTitles && (
                <Col span={24} style={{ marginTop: 20 }}>
                  <Typography.Title level={3}>
                    {tab.title}
                  </Typography.Title>
                </Col>
              )}
              {!tab.title && window.location.pathname.includes('/tramites/') && withTitles
                && (<Col span={24} style={{ marginTop: 20 }} />)}
              {renderFields(tab.fields, tab.title)}
            </React.Fragment>
          ))}
        </Row>
        <Form.Item name="id" hidden><Input /></Form.Item>
        {altaVehiculoRepuve && (
          <>
            <Form.Item hidden name="valuesRepuve"><Input /></Form.Item>
            <Form.Item hidden name="autofill"><Select trueFalse /></Form.Item>
          </>
        )}
      </Spin>
    );
  }

  if (!tipoDePadron) {
    return 'Tipo de padrón en configuración de padrón no concuerda con padrón a registrar/modificar';
  }

  return (
    <Spin spinning tip="Obteniendo catálogos...">
      <br />
      <br />
      <br />
    </Spin>
  );
};

RenderFieldsByConfig.propTypes = {
  fields: PropTypes.arrayOf(PropTypes.shape({
    id: PropTypes.number,
    hidden: PropTypes.bool,
    regex: PropTypes.string,
    max_digits: PropTypes.number,
    min_digits: PropTypes.number,
    label_de_descripcion: PropTypes.string,
    tipo_de_dato_del_campo: PropTypes.string,
    nombre_en_la_bd: PropTypes.string,
    null: PropTypes.bool,
    help_text: PropTypes.string,
    max_length: PropTypes.number,
    decimal_places: PropTypes.number,
  })),
  tipoDeAfectacion: PropTypes.oneOf(getEnumValues(afectacionesEnum)),
  tipoDePadron: PropTypes.oneOf(getEnumValues(PADRONES)),
  tipoDePadronSolicitante: PropTypes.oneOf(getEnumValues(solicitantesEnum)),
  padron: PropTypes.shape(),
  padronSolicitante: PropTypes.shape({
    id: PropTypes.number,
    tipo_de_persona: PropTypes.number,
    ciudadano: PropTypes.shape({}),
  }),
  setLoading: PropTypes.func.isRequired,
  setVisibleModalInfoRepuve: PropTypes.func,
  disabled: PropTypes.bool,
  withTitles: PropTypes.bool,
  valuesRepuve: PropTypes.shape({}),
  valuesComplementos: PropTypes.shape({}),
  tramite: PropTypes.shape(),
  recibo: PropTypes.shape(),
  plantillaDeTramite: PropTypes.shape(),
};

RenderFieldsByConfig.defaultProps = {
  fields: [],
  tipoDeAfectacion: null,
  tipoDePadron: null,
  padron: null,
  disabled: false,
  valuesRepuve: undefined,
  tipoDePadronSolicitante: null,
  setVisibleModalInfoRepuve: null,
  withTitles: true,
  valuesComplementos: null,
  padronSolicitante: null,
  tramite: null,
  recibo: null,
  plantillaDeTramite: null,
};

export default RenderFieldsByConfig;
